"use client";

import { useEffect, useRef, useState } from "react";
import { readConsent, saveConsent } from "../../library/cookie-consent";
import { forgetAdTouch } from "../../library/ad-touch";

const buttonClass = "rounded-lg border border-ink/30 px-4 py-3 text-sm font-semibold text-ink hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const settings = useRef<HTMLButtonElement>(null);

  useEffect(() => { setOpen(!readConsent()); }, []);

  function choose(allowAnalytics: boolean, allowMarketing: boolean) {
    const previous = readConsent();
    if (!saveConsent(allowAnalytics, allowMarketing)) {
      setError(true);
      return;
    }
    if (!allowAnalytics) {
      try { window.sessionStorage.removeItem("topgeo_first_touch_v1"); } catch { /* Storage unavailable. */ }
    }
    // Paid-ad touches (E12 P3): click IDs go with marketing consent, the
    // whole record when no optional consent is left.
    if (!allowMarketing) forgetAdTouch(allowAnalytics);
    if (!allowMarketing) {
      const pixel = (window as Window & { fbq?: (...args: string[]) => void }).fbq;
      pixel?.("consent", "revoke");
      // Remove this site's Meta cookies, including cookies on parent domains.
      for (const name of ["_fbp", "_fbc"]) {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        const parts = window.location.hostname.split(".");
        for (let i = 0; i < parts.length - 1; i++) {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parts.slice(i).join(".")}`;
        }
      }
    }
    if ((previous?.analytics && !allowAnalytics) || (previous?.marketing && !allowMarketing)) {
      // Restart only on withdrawal to unload already-running third-party scripts.
      window.location.reload();
      return;
    }
    setOpen(false);
    settings.current?.focus({ preventScroll: true });
  }

  return <>
    <button ref={settings} type="button" className="text-sm underline underline-offset-4 focus-visible:outline-2" onClick={() => {
      const consent = readConsent();
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
      setDetails(true);
      setOpen(true);
      requestAnimationFrame(() => heading.current?.focus());
    }}>Cookie settings</button>
    {open && <section aria-labelledby="cookie-consent-title" className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-h-[85dvh] max-w-3xl overflow-y-auto rounded-2xl border border-ink/20 bg-surface-raised p-5 text-ink shadow-2xl sm:p-6">
      <h2 id="cookie-consent-title" ref={heading} tabIndex={-1} className="text-lg font-semibold">Your privacy choices</h2>
      <p className="mt-2 text-sm leading-6">We use essential storage to remember your choices. With your permission, PostHog and TopGeo analytics help us understand visits and improve the site, and Meta cookies help measure advertising. Optional tracking stays off until you accept. Change your choice anytime in Cookie settings. <a href="/privacy" className="underline underline-offset-2">Privacy policy</a></p>
      {details && <div className="mt-4 space-y-3 rounded-lg bg-surface p-4 text-sm">
        <p><strong>Essential — always active.</strong> Remembers your privacy preferences.</p>
        <label className="flex items-start gap-3"><input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} className="mt-1 h-4 w-4" /><span><strong>Analytics</strong> — page visits, interactions and referral attribution using PostHog and TopGeo. Session recordings are disabled.</span></label>
        <label className="flex items-start gap-3"><input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} className="mt-1 h-4 w-4" /><span><strong>Marketing</strong> — Meta Pixel measures advertising performance.</span></label>
        <p className="text-xs">Turning off previously accepted tracking reloads this page.</p>
      </div>}
      {error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">Your browser could not save your choice. Please enable site storage and try again, or continue without changing your current preferences.</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className={buttonClass} onClick={() => choose(false, false)}>Reject all</button>
        <button type="button" className={buttonClass} onClick={() => choose(true, true)}>Accept all</button>
        {details ? <button type="button" className={buttonClass} onClick={() => choose(analytics, marketing)}>Save preferences</button> : <button type="button" className={buttonClass} onClick={() => setDetails(true)}>Manage preferences</button>}
      </div>
    </section>}
  </>;
}
